/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Derived live connection status for a tunneled MCP server source
 */
export const ConnectionStatus = {
  Connected: "connected",
  Inactive: "inactive",
  NeverConnected: "never_connected",
} as const;
/**
 * Derived live connection status for a tunneled MCP server source
 */
export type ConnectionStatus = OpenEnum<typeof ConnectionStatus>;

/**
 * Stored lifecycle status for a tunneled MCP server source
 */
export const TunneledMcpServerStatus = {
  Created: "created",
  Active: "active",
  Revoked: "revoked",
} as const;
/**
 * Stored lifecycle status for a tunneled MCP server source
 */
export type TunneledMcpServerStatus = OpenEnum<typeof TunneledMcpServerStatus>;

/**
 * A customer-hosted MCP server connected through a tunnel
 */
export type TunneledMcpServer = {
  /**
   * Number of active tunnel connections currently visible in Redis
   */
  activeConnectionCount: number;
  /**
   * Total MCP consumer sessions currently pinned across active tunnel connections
   */
  activeConsumerSessionCount: number;
  /**
   * Most recent agent version reported by the tunnel
   */
  agentVersion?: string | undefined;
  /**
   * Whether the owner has consented to serving this source through a public, anonymous MCP endpoint
   */
  allowPublic: boolean;
  /**
   * Derived live connection status for a tunneled MCP server source
   */
  connectionStatus: ConnectionStatus;
  /**
   * When the tunneled MCP server source was created
   */
  createdAt: Date;
  /**
   * The token-bucket capacity actually applied to this tunnel: the stored burst when a rate is stored alongside it, twice the stored rate when only a rate is stored, or the deployment default when no rate is stored (a burst stored without a rate is ignored).
   */
  effectivePublicRequestBurst: number;
  /**
   * The sustained anonymous MCP request rate actually applied to this tunnel: the stored value, or the deployment default when no rate is stored.
   */
  effectivePublicRequestRatePerSecond: number;
  /**
   * The ID of the tunneled MCP server
   */
  id: string;
  /**
   * Non-secret prefix of the tunnel key
   */
  keyPrefix: string;
  /**
   * Most recent persisted heartbeat timestamp
   */
  lastSeenAt?: Date | undefined;
  /**
   * Human-readable name for the tunneled MCP server
   */
  name: string;
  /**
   * The project ID this tunneled MCP server belongs to
   */
  projectId: string;
  /**
   * Token-bucket capacity for public_request_rate_per_second: how many requests are admitted back-to-back from an idle tunnel before admission drops to the sustained rate. Unset means twice the sustained rate.
   */
  publicRequestBurst?: number | undefined;
  /**
   * Sustained anonymous MCP requests per second admitted for this tunnel when it is served through a public MCP endpoint. Applies to every MCP interaction. Unset means the deployment-wide default applies.
   */
  publicRequestRatePerSecond?: number | undefined;
  /**
   * RFC 9728 protected resource identifier of the tunneled server, used for credential routing and as the signed caller assertion audience; never dialed by Speakeasy. The exact identifier is preserved, including trailing slashes. When unset, caller assertions use tunneled-mcp-server:<ID>
   */
  resourceIdentifier?: string | undefined;
  /**
   * Stored lifecycle status for a tunneled MCP server source
   */
  status: TunneledMcpServerStatus;
  /**
   * When the tunneled MCP server source was last updated
   */
  updatedAt: Date;
};

/** @internal */
export const ConnectionStatus$inboundSchema: z.ZodMiniType<
  ConnectionStatus,
  unknown
> = openEnums.inboundSchema(ConnectionStatus);

/** @internal */
export const TunneledMcpServerStatus$inboundSchema: z.ZodMiniType<
  TunneledMcpServerStatus,
  unknown
> = openEnums.inboundSchema(TunneledMcpServerStatus);

/** @internal */
export const TunneledMcpServer$inboundSchema: z.ZodMiniType<
  TunneledMcpServer,
  unknown
> = z.pipe(
  z.object({
    active_connection_count: z.int(),
    active_consumer_session_count: z.int(),
    agent_version: z.optional(z.string()),
    allow_public: z.boolean(),
    connection_status: ConnectionStatus$inboundSchema,
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    effective_public_request_burst: z.int(),
    effective_public_request_rate_per_second: z.int(),
    id: z.string(),
    key_prefix: z.string(),
    last_seen_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    name: z.string(),
    project_id: z.string(),
    public_request_burst: z.optional(z.int()),
    public_request_rate_per_second: z.optional(z.int()),
    resource_identifier: z.optional(z.string()),
    status: TunneledMcpServerStatus$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_connection_count": "activeConnectionCount",
      "active_consumer_session_count": "activeConsumerSessionCount",
      "agent_version": "agentVersion",
      "allow_public": "allowPublic",
      "connection_status": "connectionStatus",
      "created_at": "createdAt",
      "effective_public_request_burst": "effectivePublicRequestBurst",
      "effective_public_request_rate_per_second":
        "effectivePublicRequestRatePerSecond",
      "key_prefix": "keyPrefix",
      "last_seen_at": "lastSeenAt",
      "project_id": "projectId",
      "public_request_burst": "publicRequestBurst",
      "public_request_rate_per_second": "publicRequestRatePerSecond",
      "resource_identifier": "resourceIdentifier",
      "updated_at": "updatedAt",
    });
  }),
);

export function tunneledMcpServerFromJSON(
  jsonString: string,
): SafeParseResult<TunneledMcpServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => TunneledMcpServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'TunneledMcpServer' from JSON`,
  );
}
